import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Check, Quote, Sparkles } from 'lucide-react';
import { CTAPair, FaqAccordion, Notice } from '@kollez/design-system';
import { PageMeta } from '@/components/PageMeta';

/**
 * /customers — long-form story format ("Three customers, three stories").
 *
 * Every other marketing page uses a hero → grid → testimonials shape.
 * /customers uses vertical long-form: one story at a time, alternating
 * left/right, with before/after data strip, pull quote, and a stage-by-
 * stage adoption timeline. Reads like a case-study PDF, feels like a
 * conversation.
 *
 * Design spec: docs/design/marketing/_per-page-design-language.md
 * (customers gets the long-form story format).
 *
 * Full per-college case-study pages arrive as institutions sign off on
 * their quotes and metrics. Until then, this page tells three
 * anchor stories in depth.
 */

interface StoryStage {
  when: string;
  what: string;
  detail: string;
}

interface Story {
  slug: string;
  logo: string;
  title: string;
  segment: string;
  location: string;
  headline: string;
  intro: string;
  challenge: {
    heading: string;
    body: string;
    bullets: string[];
  };
  approach: {
    heading: string;
    body: string;
    bullets: string[];
  };
  before: Array<{ number: string; label: string }>;
  after: Array<{ number: string; label: string }>;
  quote: { text: string; author: string; role: string };
  timeline: StoryStage[];
  ctaTo: string;
  ctaLabel: string;
}

const STORIES: Story[] = [
  {
    slug: 'malla-reddy-group',
    logo: 'MRG',
    title: 'Malla Reddy Group',
    segment: '5 colleges · engineering',
    location: 'Hyderabad',
    headline: 'From 34 drives to 87 in one season — five colleges opening Kollez by 8 am.',
    intro:
      'Malla Reddy Group runs placement across five engineering colleges. Before Kollez, each college ran its own drives on WhatsApp groups and shared Excel sheets. The group office saw the numbers only after every drive was done, and could never compare like-for-like.',
    challenge: {
      heading: 'The problem',
      body: 'Five placement cells, five different ways of tracking a drive. Group leadership had no live view; every quarterly review meant a week of copy-pasting numbers into one deck.',
      bullets: [
        'No group-wide roll-up until the season ended',
        'Drives duplicated across colleges — one company would visit each campus separately',
        'NIRF reporting each year meant reconstructing the year from scratch',
      ],
    },
    approach: {
      heading: 'What changed',
      body: 'Every college moved to one register during the 2025-26 pilot. Group leadership got a single roll-up at 8 am. Nexie drafted NIRF from the same data instead of the placement team retyping it.',
      bullets: [
        'One register per college, one roll-up for the group',
        'Drives visible across all five campuses; shared shortlists where MoUs allowed it',
        'Nexie drafted the NIRF and NAAC narratives; team edited and signed off',
      ],
    },
    before: [
      { number: '34',    label: 'drives per season' },
      { number: '1 wk',  label: 'to write NIRF' },
      { number: 'Monthly', label: 'group visibility' },
    ],
    after: [
      { number: '87',      label: 'drives per season' },
      { number: '1 morning', label: 'to draft NIRF' },
      { number: 'Live',    label: 'group visibility' },
    ],
    quote: {
      text: 'We ran 87 drives last season, up from 34. Every college in the group opens Kollez by 8 am and knows what to chase before their first coffee.',
      author: 'Dr. Ramesh Malla',
      role: 'Director, Placements · Malla Reddy Group',
    },
    timeline: [
      { when: 'Week 1',   what: 'MoU + pilot signed',       detail: 'One college onboarded; four more scheduled.' },
      { when: 'Week 4',   what: 'All five colleges live',   detail: 'Group roll-up dashboard live at 8 am.' },
      { when: 'Month 3',  what: '30+ drives in one season', detail: 'Already matching prior full-year total.' },
      { when: 'Month 6',  what: 'NIRF draft in a morning',  detail: 'Nexie drafted; placement team edited and signed off in one working day.' },
    ],
    ctaTo: '/for-institutions',
    ctaLabel: 'Explore Kollez for institutions',
  },
  {
    slug: 'godavari-institute',
    logo: 'GIT',
    title: 'Godavari Institute of Technology',
    segment: '1 college · engineering',
    location: 'Rajahmundry',
    headline: 'Half the TPO team, twice the placement rate — one register at a time.',
    intro:
      'Godavari Institute is a single-campus engineering college with a two-person placement office. Before Kollez, the team spent most of every drive verifying student data by hand — checking marksheets against the roster, chasing signatures on offer letters.',
    challenge: {
      heading: 'The problem',
      body: 'Two placement officers, 1,200 final-year students, and every offer letter checked by hand. One wrong-CTC offer in 2024 nearly cost the college its top-recruiter relationship.',
      bullets: [
        'Manual marksheet verification took 3-5 hours per drive',
        'Offer letters signed without automated sign-off checks',
        'No verifiable audit trail when a company asked "what did you send that student"',
      ],
    },
    approach: {
      heading: 'What changed',
      body: 'Godavari moved to Kollez during the 2024-25 pilot. DigiLocker did the marks verification. Sign-off checks caught the four things that go wrong most often. The 10-minute undo window turned a scare into a save.',
      bullets: [
        'DigiLocker-verified marks on every profile — no manual check',
        'Automated sign-off checks (name mismatch, CTC out of policy, wrong signatory, student already placed)',
        '10-minute undo window on every accepted offer',
      ],
    },
    before: [
      { number: '3-5 hr', label: 'per-drive verification' },
      { number: '1',      label: 'near-miss wrong-CTC offer' },
      { number: 'Manual', label: 'offer audit trail' },
    ],
    after: [
      { number: '5 min', label: 'per-drive verification' },
      { number: '0',      label: 'offer-letter errors' },
      { number: 'Signed', label: 'audit trail per offer' },
    ],
    quote: {
      text: 'Sign-off checks caught what would have been a wrong-CTC offer on our second-ever drive. That alone paid for the year.',
      author: 'Prof. Suresh B.',
      role: 'Principal · Godavari Institute',
    },
    timeline: [
      { when: 'Day 1',    what: 'Pilot signed',              detail: 'One college, one placement season, zero cost.' },
      { when: 'Day 3',    what: 'Roster imported',           detail: '1,200 final-year students, CSV mapped in one session.' },
      { when: 'Week 2',   what: 'First drive shipped',       detail: '412 applications, 64 shortlisted, 21 offers — same pipeline the group runs.' },
      { when: 'Season end', what: 'Doubled placement rate', detail: 'Same team, same students, twice the offers accepted.' },
    ],
    ctaTo: '/for-institutions',
    ctaLabel: 'Explore Kollez for institutions',
  },
  {
    slug: 'wipro-elite-nth',
    logo: 'W',
    title: 'Wipro Elite NTH',
    segment: 'Partner · IT services',
    location: 'Bengaluru',
    headline: 'One post reached seven campuses — time-to-hire collapsed from six weeks to three days.',
    intro:
      'Wipro Elite NTH runs the top-of-funnel Wipro campus-hiring programme. Before Kollez, the TA team travelled to each partner campus separately, ran the same test in different rooms, and reconciled results in Excel.',
    challenge: {
      heading: 'The problem',
      body: 'Seven partner campuses, one Elite NTH test. Every campus visit meant a full day of test administration + a week of verification. Time-to-hire ran six weeks per drive.',
      bullets: [
        'Same test administered seven times across seven campuses',
        'Manual verification of every candidate\'s marksheet',
        'Six-week gap between test day and offer letter',
      ],
    },
    approach: {
      heading: 'What changed',
      body: 'One posting on Kollez reached every partner campus. Nexie ranked the shortlist with cited reasons; the Wipro TA team approved before any candidate was contacted. Marks were DigiLocker-verified before the shortlist even ran.',
      bullets: [
        'One posting → 7 partner campuses, simultaneously',
        'Nexie-ranked shortlist with plain-English reasons',
        'DigiLocker-verified marks; contact hidden until shortlist',
      ],
    },
    before: [
      { number: '7 visits', label: 'per drive' },
      { number: '6 weeks',  label: 'time to hire' },
      { number: 'Manual',   label: 'shortlisting' },
    ],
    after: [
      { number: '1 post',   label: 'per drive' },
      { number: '3 days',   label: 'time to hire' },
      { number: 'Nexie',    label: 'ranked, cited, human-approved' },
    ],
    quote: {
      text: 'Every candidate\'s marks were already verified before we saw them. That is a rare thing in India campus hiring.',
      author: 'Priya Ramesh',
      role: 'Campus TA lead · Wipro Elite NTH',
    },
    timeline: [
      { when: 'Kickoff',  what: 'Partner-network MoU',       detail: 'Wipro Elite NTH verified as a partner; seven college MoUs enabled.' },
      { when: 'Day 1',    what: 'One post, seven campuses',  detail: 'Posted role, uploaded JD, Nexie drafted eligibility filter.' },
      { when: 'Day 2',    what: 'Nexie shortlist reviewed',  detail: 'Top 40 candidates from 412 applicants, cited reasons.' },
      { when: 'Day 3',    what: 'Offers released',           detail: '21 offers signed, 18 accepted — one drive, one week.' },
    ],
    ctaTo: '/for-partners',
    ctaLabel: 'Explore Kollez for partners',
  },
];

/* ────────── sub-components ────────── */

function CustomerLogoTile({ logo }: { logo: string }) {
  return (
    <span
      className="flex items-center justify-center rounded-md font-serif font-semibold flex-shrink-0"
      style={{
        width: 56, height: 56,
        background: 'rgb(var(--bg-info))',
        color: 'rgb(var(--ink-accent))',
        fontSize: 18,
      }}
    >
      {logo}
    </span>
  );
}

function BeforeAfterStrip({ before, after }: { before: Story['before']; after: Story['after'] }) {
  return (
    <div className="grid gap-4 sm:grid-cols-[1fr_auto_1fr] items-stretch">
      {/* BEFORE */}
      <div className="rounded-lg p-5 grid gap-2" style={{ background: 'rgb(var(--bg-secondary))' }}>
        <p className="eyebrow m-0 text-xs" style={{ color: 'rgb(var(--ink-tertiary))' }}>Before Kollez</p>
        <div className="grid grid-cols-3 gap-3 mt-1">
          {before.map((m) => (
            <div key={m.label} className="text-center">
              <div
                className="font-serif tabular-nums"
                style={{ fontSize: 20, fontWeight: 500, color: 'rgb(var(--ink-tertiary))' }}
              >
                {m.number}
              </div>
              <div className="text-[11px] mt-0.5" style={{ color: 'rgb(var(--ink-tertiary))' }}>{m.label}</div>
            </div>
          ))}
        </div>
      </div>
      {/* ARROW */}
      <div className="hidden sm:flex items-center justify-center px-1" aria-hidden="true">
        <ArrowRight size={24} style={{ color: 'rgb(var(--ink-accent))' }} />
      </div>
      {/* AFTER */}
      <div
        className="rounded-lg p-5 grid gap-2"
        style={{ background: 'rgb(var(--bg-success))', border: '1px solid rgb(var(--ink-success))' }}
      >
        <p className="eyebrow m-0 text-xs" style={{ color: 'rgb(var(--ink-success))' }}>With Kollez</p>
        <div className="grid grid-cols-3 gap-3 mt-1">
          {after.map((m) => (
            <div key={m.label} className="text-center">
              <div
                className="font-serif tabular-nums"
                style={{ fontSize: 22, fontWeight: 500, color: 'rgb(var(--ink-primary))' }}
              >
                {m.number}
              </div>
              <div className="text-[11px] mt-0.5" style={{ color: 'rgb(var(--ink-primary))' }}>{m.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AdoptionTimeline({ timeline }: { timeline: StoryStage[] }) {
  return (
    <ol className="grid gap-4 relative">
      {timeline.map((t, i) => (
        <li key={i} className="grid gap-1 pl-9 relative">
          <span
            className="absolute left-0 top-0.5 flex items-center justify-center rounded-full"
            style={{
              width: 24, height: 24,
              background: 'rgb(var(--bg-info))',
              color: 'rgb(var(--ink-accent))',
            }}
          >
            <Check size={12} />
          </span>
          {i < timeline.length - 1 && (
            <span
              className="absolute left-3 top-6 bottom-[-16px] w-px"
              style={{ background: 'rgb(var(--line-tertiary))' }}
              aria-hidden="true"
            />
          )}
          <div className="flex items-baseline gap-2 flex-wrap">
            <span
              className="text-[11px] uppercase tracking-wider font-medium"
              style={{ color: 'rgb(var(--ink-accent))' }}
            >
              {t.when}
            </span>
            <span className="text-sm font-medium">{t.what}</span>
          </div>
          <p className="text-sm text-ink-secondary m-0">{t.detail}</p>
        </li>
      ))}
    </ol>
  );
}

function StorySection({ story, index }: { story: Story; index: number }) {
  const contentFirst = index % 2 === 0;

  return (
    <article
      className="mx-auto max-w-page-wide px-5 lg:px-10 py-16 lg:py-20 grid gap-10"
      id={story.slug}
    >
      {/* HEADER — full width */}
      <header className="grid gap-4 max-w-3xl">
        <div className="flex items-center gap-4">
          <CustomerLogoTile logo={story.logo} />
          <div className="min-w-0">
            <p className="eyebrow m-0">Case study · {String(index + 1).padStart(2, '0')} of {String(STORIES.length).padStart(2, '0')}</p>
            <h2 className="h-serif text-2xl leading-tight mt-1">{story.title}</h2>
            <div className="text-sm text-ink-tertiary mt-0.5">{story.segment} · {story.location}</div>
          </div>
        </div>
        <h3
          className="text-ink-primary"
          style={{
            fontFamily: 'Newsreader, Georgia, serif',
            fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
            lineHeight: 1.15,
            fontWeight: 500,
            margin: 0,
            letterSpacing: '-0.005em',
          }}
        >
          {story.headline}
        </h3>
        <p className="text-lg text-ink-secondary leading-8 m-0">{story.intro}</p>
      </header>

      {/* CHALLENGE + APPROACH — two-col */}
      <div className="grid gap-8 lg:grid-cols-2">
        <div className={contentFirst ? '' : 'lg:order-2'}>
          <p className="eyebrow m-0 text-xs">{story.challenge.heading}</p>
          <p className="mt-2 text-ink-secondary">{story.challenge.body}</p>
          <ul className="mt-4 grid gap-2 text-sm text-ink-secondary">
            {story.challenge.bullets.map((b) => (
              <li key={b} className="flex gap-2">
                <span
                  className="mt-1.5 flex-shrink-0"
                  style={{
                    width: 6, height: 6, borderRadius: 999,
                    background: 'rgb(var(--ink-tertiary))',
                    display: 'inline-block',
                  }}
                />
                {b}
              </li>
            ))}
          </ul>
        </div>
        <div className={contentFirst ? '' : 'lg:order-1'}>
          <p className="eyebrow m-0 text-xs flex items-center gap-1.5">
            <Sparkles size={12} />
            {story.approach.heading}
          </p>
          <p className="mt-2 text-ink-secondary">{story.approach.body}</p>
          <ul className="mt-4 grid gap-2 text-sm text-ink-secondary">
            {story.approach.bullets.map((b) => (
              <li key={b} className="flex gap-2">
                <Check size={14} className="mt-1 flex-shrink-0" style={{ color: 'rgb(var(--ink-success))' }} />
                {b}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* BEFORE / AFTER STRIP — full width */}
      <BeforeAfterStrip before={story.before} after={story.after} />

      {/* PULL QUOTE — inkwell */}
      <blockquote
        className="inkwell rounded-lg p-8 lg:p-10 grid gap-4"
        style={{ position: 'relative' }}
      >
        <Quote size={28} style={{ color: 'rgba(232,212,122,0.4)' }} aria-hidden="true" />
        <p
          className="font-serif italic m-0"
          style={{
            fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
            lineHeight: 1.35,
            color: 'rgba(255,255,255,0.92)',
            textWrap: 'pretty' as never,
          }}
        >
          {story.quote.text}
        </p>
        <cite className="not-italic text-sm mt-2" style={{ color: 'rgba(255,255,255,0.6)' }}>
          — {story.quote.author}, {story.quote.role}
        </cite>
      </blockquote>

      {/* TIMELINE + CTA — two-col */}
      <div className="grid gap-8 lg:grid-cols-[1fr_0.8fr] items-start">
        <div>
          <p className="eyebrow m-0 text-xs flex items-center gap-1.5">
            <Calendar size={12} />
            Adoption timeline
          </p>
          <div className="mt-4">
            <AdoptionTimeline timeline={story.timeline} />
          </div>
        </div>
        <div className="card card-pad grid gap-3 content-start">
          <h4 className="font-serif text-lg m-0">Run your own drive on this pattern.</h4>
          <p className="helper m-0">
            Same register, same sign-off checks, same 10-minute undo. Start with a free pilot for one college — or one campaign for one campus.
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            <Link to="/contact?topic=demo" className="btn btn-primary btn-sm">Book a 30-min demo</Link>
            <Link to={story.ctaTo} className="btn btn-ghost btn-sm">{story.ctaLabel} →</Link>
          </div>
        </div>
      </div>
    </article>
  );
}

/* ────────── page ────────── */

export function CustomersPage() {
  return (
    <>
      <PageMeta
        title="Customers"
        description="How Indian colleges, groups and recruiters run placement on Kollez. Three long-form case studies with before/after numbers, adoption timelines, and honest quotes."
        canonical="/customers"
      />

      {/* HERO */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 pt-16 pb-8 lg:pt-24 grid gap-6">
        <div className="max-w-3xl">
          <p className="eyebrow flex items-center gap-1.5">
            <Sparkles size={12} />
            Customers on Kollez
          </p>
          <h1
            className="text-ink-primary mt-2"
            style={{
              fontFamily: 'Newsreader, Georgia, serif',
              fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
              lineHeight: 1.05,
              fontWeight: 500,
              letterSpacing: '-0.01em',
              margin: 0,
            }}
          >
            Three customers.<br />
            Three stories. Told in full.
          </h1>
          <p className="mt-6 text-lg leading-8 text-ink-secondary">
            One education group, one standalone college, one hiring partner.
            Before, what changed, the numbers, the timeline, and the quote —
            no card grid, no summary. Read one; skip to the next.
          </p>
        </div>

        {/* TABLE OF CONTENTS */}
        <nav aria-label="Case studies on this page" className="grid gap-2 mt-2">
          <p className="eyebrow m-0 text-xs">On this page</p>
          <ol className="grid gap-2 text-sm">
            {STORIES.map((s, i) => (
              <li key={s.slug}>
                <a
                  href={`#${s.slug}`}
                  className="inline-flex items-baseline gap-3 hover:opacity-75"
                >
                  <span
                    className="tabular-nums text-xs"
                    style={{ color: 'rgb(var(--ink-accent))' }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="font-medium">{s.title}</span>
                  <span className="text-ink-tertiary hidden sm:inline">— {s.headline}</span>
                </a>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      {/* STORIES — vertical stack, separators between */}
      {STORIES.map((s, i) => (
        <div key={s.slug} className={i > 0 ? 'border-t border-line-tertiary' : ''}>
          <StorySection story={s} index={i} />
        </div>
      ))}

      {/* HONEST-DISCLOSURE NOTICE */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 pb-16">
        <Notice tone="info">
          Full per-college case-study pages (with photos and signed quote
          approvals) arrive as institutions sign off on their metrics.
          Meanwhile — email{' '}
          <a className="link" href="mailto:sales@kollez.ai">sales@kollez.ai</a>{' '}
          for detailed references, or{' '}
          <Link className="link" to="/contact?topic=demo">book a demo</Link>{' '}
          and we&apos;ll walk through a specific drive on a call.
        </Notice>
      </section>

      {/* FAQ */}
      <section className="border-t border-line-tertiary bg-primary py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-10">
          <FaqAccordion
            heading="Common questions from prospective customers"
            emitJsonLd
            items={[
              { q: 'Can I speak with an existing customer before signing?',
                a: 'Yes. Email sales@kollez.ai and we\'ll connect you with a placement head at a peer institution. We ask first, we don\'t volunteer names.' },
              { q: 'What size of institution does Kollez fit?',
                a: 'Anywhere from a single 200-student engineering college to a 50-college multi-university group. The pilot tier fits one college; the Group tier scales to whole trusts.' },
              { q: 'Can I see the group roll-up on a demo?',
                a: 'Yes. Book a 30-min demo and we\'ll walk through a real (anonymised) group roll-up dashboard, live.' },
              { q: 'How long before a new customer sees their first drive?',
                a: 'A pilot college is live in 3-4 working days. Group-wide rollout across 10+ colleges takes 2-3 weeks including MoU sign-off. We stay on-call through your first drive.' },
              { q: 'Do you have customers outside India?',
                a: 'Not yet. Kollez is built for the Indian placement season (NIRF/NAAC/AICTE, DPDP, INR-native, +91 mobile-first). We\'ll open other geographies when the second-side product is proven.' },
            ]}
          />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-page-wide px-5 py-16 lg:px-10 lg:py-20 text-center">
        <h2 className="display" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
          Ready to be the next story on this page?
        </h2>
        <p className="mt-4 text-ink-secondary max-w-xl mx-auto">
          Free pilot for one college, one season. No card, no PO, no lock-in.
        </p>
        <div className="mt-8 flex justify-center">
          <CTAPair
            primary={{ label: 'Book a 30-min demo', to: '/contact?topic=demo' }}
            secondary={{ label: 'Start a free pilot', to: '/register/institution?plan=pilot' }}
            align="center"
          />
        </div>
      </section>
    </>
  );
}
